
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Navigation, Clock, Car, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface CaregiverLocationProps {
  caregiverId: string;
  caregiverName: string;
  caregiverImage: string;
  estimatedArrival: string;
  currentLocation?: string;
}

const CaregiverTracker: React.FC<CaregiverLocationProps> = ({
  caregiverId,
  caregiverName,
  caregiverImage,
  estimatedArrival,
  currentLocation = "En route to your location",
}) => {
  const [progress, setProgress] = useState(10);
  
  // Simulate progress updates
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        // Randomly increase progress between 1-3%
        const newProgress = prevProgress + Math.floor(Math.random() * 3) + 1;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 3000);
    
    return () => {
      clearInterval(timer);
    };
  }, []);

  // Calculate estimated minutes based on progress
  const estimatedMinutes = Math.ceil((100 - progress) / 5);
  
  // Get initials for fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };
  
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-care-100">
      <div className="p-5 bg-care-50">
        <h3 className="text-lg font-semibold text-care-900">Caregiver Tracker</h3>
        <p className="text-care-600 text-sm">Track your caregiver's arrival in real-time</p>
      </div>
      
      <div className="p-5">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="h-16 w-16 border-2 border-care-100">
            <AvatarImage src={caregiverImage} alt={caregiverName} />
            <AvatarFallback>{getInitials(caregiverName)}</AvatarFallback>
          </Avatar>
          <div>
            <h4 className="font-semibold text-lg text-care-800">{caregiverName}</h4>
            <div className="flex items-center text-care-600 text-sm gap-1 mt-1">
              <Car className="h-4 w-4" />
              <span>Arriving in {estimatedMinutes} minutes</span>
            </div>
          </div>
        </div>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-care-50 rounded-lg text-care-600">
              <Navigation className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-sm text-care-800">Current Status</p>
              <p className="text-sm text-care-600">{currentLocation}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="p-2 bg-care-50 rounded-lg text-care-600">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-sm text-care-800">Estimated Arrival</p>
              <p className="text-sm text-care-600">{estimatedArrival}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="p-2 bg-care-50 rounded-lg text-care-600">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-sm text-care-800">Destination</p>
              <p className="text-sm text-care-600">Your registered address</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-2 mb-6">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-care-800">Arrival Progress</span>
            <span className="text-care-600">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between text-xs text-care-500">
            <span>Departed</span>
            <span>En Route</span>
            <span>Arrived</span>
          </div>
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 gap-1 border-care-200 text-care-700"
          >
            <Phone className="h-4 w-4" /> Call
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 gap-1 border-care-200 text-care-700"
          >
            <MessageSquare className="h-4 w-4" /> Message
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaregiverTracker;
